import React, { FC, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  alpha,
  useTheme,
  Stack,
  LinearProgress,
  Icon,
  CircularProgress,
  styled,
} from "@mui/material";
import ReactFlow, {
  Background,
  Controls,
  MarkerType,
  Position,
  Edge,
  Node,
} from "reactflow";
import "reactflow/dist/style.css";
import dayjs from "dayjs";
import { ExtendedBatch, IngredientNodeData, IngredientOption } from "./types";

interface RecipeGraphPanelProps {
  recipe: {
    name: string;
    ingredients: Array<{
      item_id: string;
      ingredient_id: string;
    }>;
  };
  ingredientOptions: IngredientOption[];
  onNodeClick: (event: React.MouseEvent, node: Node) => void;
  batchDataCache: Record<
    string,
    {
      batches: ExtendedBatch[];
      totalCost: number;
      averageCost: number;
      totalQuantity: number;
      lastFetched: number;
    }
  >;
  getBatchData: (ingredientId: string) => Promise<{
    batches: ExtendedBatch[];
    totalCost: number;
    averageCost: number;
    totalQuantity: number;
    lastFetched: number;
  } | null>;
}

// Add styled component for batch node hover effect
const BatchNodeBox = styled(Box)(({ theme }) => ({
  "& .batch-node": {
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      opacity: "1 !important",
      transform: "translateY(-2px) !important",
    },
  },
}));

export const RecipeGraphPanel: FC<RecipeGraphPanelProps> = ({
  recipe,
  ingredientOptions,
  onNodeClick,
  batchDataCache,
  getBatchData,
}) => {
  const theme = useTheme();
  const [loading, setLoading] = React.useState(false);

  // Load batches only once when component mounts or recipe changes
  React.useEffect(() => {
    let mounted = true;
    const loadBatches = async () => {
      if (!mounted) return;
      setLoading(true);

      try {
        // Create a Set of unique ingredient IDs to prevent duplicates
        const uniqueIngredientIds = new Set(
          recipe.ingredients.map((ing) => ing.ingredient_id)
        );

        // Load any missing batch data
        const promises = Array.from(uniqueIngredientIds).map((ingredientId) => {
          if (!batchDataCache[ingredientId]) {
            return getBatchData(ingredientId);
          }
          return Promise.resolve(null);
        });

        await Promise.all(promises);
      } catch (error) {
        console.error("Error loading batches:", error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    if (recipe.ingredients.length > 0) {
      loadBatches();
    }

    return () => {
      mounted = false;
    };
  }, [recipe.ingredients, getBatchData, batchDataCache]);

  // Memoize nodes and edges generation
  const { nodes, edges } = useMemo(() => {
    // Recipe node
    const recipeNode: Node = {
      id: "recipe",
      type: "input",
      data: { label: recipe.name || "Recipe" },
      position: { x: 250, y: 200 },
      sourcePosition: Position.Right,
      style: {
        background: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        border: "none",
        borderRadius: "8px",
        padding: "10px 20px",
        width: 180,
      },
    };

    const ingredientNodes: Node[] = [];
    const batchNodes: Node[] = [];
    const ingredientEdges: Edge[] = [];
    const batchEdges: Edge[] = [];

    recipe.ingredients.forEach((ing, index) => {
      const ingredient = ingredientOptions.find(
        (opt) => opt.id === ing.item_id
      );
      if (!ingredient) return;

      const batchInfo = batchDataCache[ing.ingredient_id];
      if (!batchInfo) return;

      const hasExpiredBatches = batchInfo.batches.some((batch) =>
        dayjs(batch.expiry_date).isBefore(dayjs())
      );
      const hasExpiringBatches = batchInfo.batches.some((batch) =>
        dayjs(batch.expiry_date).isBefore(dayjs().add(30, "day"))
      );

      // Improved vertical positioning with better spacing
      const ingredientSpacing = 300;
      const ingredientY = 50 + index * ingredientSpacing;

      // Update ingredient node content
      const nodeContent = (
        <Box sx={{ p: 1, cursor: "pointer" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1.5,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {ingredient.name}
            </Typography>
            <Icon
              fontSize="small"
              color={
                hasExpiredBatches
                  ? "error"
                  : hasExpiringBatches
                  ? "warning"
                  : "success"
              }
            >
              {hasExpiredBatches
                ? "error"
                : hasExpiringBatches
                ? "warning"
                : "check_circle"}
            </Icon>
          </Box>
          <Stack spacing={1}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Icon fontSize="small" sx={{ color: theme.palette.primary.main }}>
                inventory
              </Icon>
              <Typography variant="body2">
                {batchInfo.totalQuantity.toFixed(1)} {ingredient.unit}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Icon fontSize="small" sx={{ color: theme.palette.primary.main }}>
                payments
              </Icon>
              <Typography variant="body2">
                {batchInfo.averageCost.toFixed(3)} BHD/{ingredient.unit}
              </Typography>
            </Box>
          </Stack>
        </Box>
      );

      const ingredientNode: Node = {
        id: `ing-${index}`,
        data: {
          label: nodeContent,
          ingredient,
          batches: batchInfo.batches,
          totalCost: batchInfo.totalCost,
          averageCost: batchInfo.averageCost,
          totalQuantity: batchInfo.totalQuantity,
          isExpanded: true,
        },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        position: {
          x: 600,
          y: ingredientY,
        },
        style: {
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
          border: `2px solid ${
            hasExpiredBatches
              ? theme.palette.error.main
              : hasExpiringBatches
              ? theme.palette.warning.main
              : theme.palette.success.main
          }`,
          borderRadius: "12px",
          padding: "12px",
          width: 250,
          boxShadow: `0 2px 4px ${alpha(theme.palette.common.black, 0.05)}`,
          transition: "all 0.2s ease-in-out",
        },
      };

      ingredientNodes.push(ingredientNode);
      ingredientEdges.push({
        id: `edge-${ingredientNode.id}`,
        source: "recipe",
        target: ingredientNode.id,
        type: "smoothstep",
        animated: true,
        style: { stroke: theme.palette.primary.main },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: theme.palette.primary.main,
        },
      });

      // Add batch nodes
      const sortedBatches = [...batchInfo.batches].sort((a, b) =>
        dayjs(a.expiry_date).diff(dayjs(b.expiry_date))
      );

      sortedBatches.forEach((batch, batchIndex) => {
        const totalBatches = sortedBatches.length;
        const batchSpacing = 100;
        const batchGroupHeight = (totalBatches - 1) * batchSpacing;
        const batchY =
          ingredientY - batchGroupHeight / 2 + batchIndex * batchSpacing;

        const isExpired = dayjs(batch.expiry_date).isBefore(dayjs());
        const isExpiring = dayjs(batch.expiry_date).isBefore(
          dayjs().add(30, "day")
        );
        const isEarliestExpiring = batchIndex === 0;

        const batchContent = (
          <Box sx={{ p: 1 }}>
            <Stack spacing={1}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "1rem",
                    color: isEarliestExpiring
                      ? theme.palette.primary.main
                      : "text.primary",
                    fontWeight: isEarliestExpiring ? 600 : 400,
                  }}
                >
                  {batch.brand_name || "No Brand"}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    bgcolor: alpha(theme.palette.grey[500], 0.1),
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                  }}
                >
                  #{batch.batch_number}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Icon
                  fontSize="small"
                  color={
                    isExpired ? "error" : isExpiring ? "warning" : "success"
                  }
                  sx={{ opacity: isEarliestExpiring ? 1 : 0.6 }}
                >
                  {isExpired
                    ? "error"
                    : isExpiring
                    ? "warning"
                    : "check_circle"}
                </Icon>
                <Typography
                  variant="body2"
                  sx={{
                    color: isExpired
                      ? "error.main"
                      : isExpiring
                      ? "warning.main"
                      : "success.main",
                    fontWeight: isEarliestExpiring ? 600 : 400,
                    opacity: isEarliestExpiring ? 1 : 0.7,
                  }}
                >
                  {dayjs(batch.expiry_date).format("MMM D, YYYY")}
                </Typography>
              </Box>

              <Box>
                <LinearProgress
                  variant="determinate"
                  value={(batch.remaining_quantity / batch.quantity) * 100}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: alpha(theme.palette.grey[500], 0.12),
                    opacity: isEarliestExpiring ? 1 : 0.6,
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 4,
                      bgcolor: isEarliestExpiring ? "primary.main" : "grey.500",
                    },
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.5,
                    fontWeight: isEarliestExpiring ? 600 : 400,
                    opacity: isEarliestExpiring ? 1 : 0.7,
                  }}
                >
                  {batch.remaining_quantity}/{batch.quantity} {batch.unit}
                </Typography>
              </Box>
            </Stack>
          </Box>
        );

        const batchNode: Node = {
          id: `batch-${index}-${batchIndex}`,
          data: { label: batchContent },
          targetPosition: Position.Left,
          position: {
            x: 1000,
            y: batchY,
          },
          style: {
            background: theme.palette.background.paper,
            color: theme.palette.text.primary,
            border: `2px solid ${
              isExpired
                ? theme.palette.error.main
                : isExpiring
                ? theme.palette.warning.main
                : theme.palette.success.main
            }`,
            borderRadius: "12px",
            padding: "8px",
            width: 280,
            boxShadow: isEarliestExpiring
              ? `0 4px 8px ${alpha(theme.palette.primary.main, 0.15)}`
              : `0 2px 4px ${alpha(theme.palette.common.black, 0.05)}`,
            opacity: isEarliestExpiring ? 1 : 0.85,
            cursor: "pointer",
            transition: "all 0.2s ease-in-out",
          },
        };

        batchNodes.push(batchNode);
        batchEdges.push({
          id: `edge-${batchNode.id}`,
          source: ingredientNode.id,
          target: batchNode.id,
          type: "smoothstep",
          animated: false,
          style: {
            stroke: isEarliestExpiring
              ? theme.palette.primary.main
              : theme.palette.secondary.main,
            strokeDasharray: isEarliestExpiring ? "5,5" : "none",
            opacity: ingredientNode.data.isExpanded
              ? isEarliestExpiring
                ? 1
                : 0.6
              : 0,
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: isEarliestExpiring
              ? theme.palette.primary.main
              : theme.palette.secondary.main,
          },
        });
      });
    });

    return {
      nodes: [recipeNode, ...ingredientNodes, ...batchNodes],
      edges: [...ingredientEdges, ...batchEdges],
    };
  }, [recipe, ingredientOptions, batchDataCache, theme]);

  if (loading) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <BatchNodeBox sx={{ height: "calc(100% - 48px)", pt: 2 }}>
      <Paper
        elevation={0}
        variant="outlined"
        sx={{ height: "100%", overflow: "hidden" }}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          fitView
          defaultViewport={{ x: 0, y: 0, zoom: 1 }}
          onNodeClick={onNodeClick}
          fitViewOptions={{
            padding: 0.2,
            includeHiddenNodes: true,
          }}
          minZoom={0.5}
          maxZoom={1.5}
        >
          <Background />
          <Controls />
        </ReactFlow>
      </Paper>
    </BatchNodeBox>
  );
};
